<script lang="ts">
  /*
   * A Material 3 tonal dialog over the native <dialog> element.
   *
   * Native rather than a hand-rolled overlay: `showModal()` gives the focus trap, the inert background,
   * Escape, and the top layer for free — all things a div gets wrong quietly. The app's dialog shape
   * (28px corner, tonal surface) is kept so it reads as the same product.
   */
  import type { Snippet } from 'svelte'
  import { ripple } from '../ripple'

  let {
    open = $bindable(false),
    title,
    confirmLabel,
    destructive = false,
    busy = false,
    onconfirm,
    children,
  }: {
    open?: boolean
    title: string
    confirmLabel: string
    destructive?: boolean
    busy?: boolean
    onconfirm: () => void
    children: Snippet
  } = $props()

  let dialog = $state<HTMLDialogElement | null>(null)

  $effect(() => {
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    else if (!open && dialog.open) dialog.close()
  })
</script>

<dialog bind:this={dialog} onclose={() => (open = false)} aria-labelledby="dialog-title">
  <h2 id="dialog-title">{title}</h2>
  <div class="body">{@render children()}</div>
  <div class="actions">
    <button class="btn btn-text rip" use:ripple type="button" onclick={() => (open = false)} disabled={busy}>
      Cancel
    </button>
    <button
      class="btn rip"
      class:btn-danger={destructive}
      class:btn-accent={!destructive}
      use:ripple
      type="button"
      onclick={onconfirm}
      disabled={busy}
    >
      {confirmLabel}
    </button>
  </div>
</dialog>

<style>
  dialog {
    border: 0;
    padding: var(--space-6);
    border-radius: var(--bj-dialog-radius);
    background: var(--bj-dialog-surface);
    color: var(--bj-on-surface);
    box-shadow: var(--elev-3);
    max-width: min(28rem, calc(100vw - var(--space-8)));
    width: 100%;
  }
  dialog::backdrop {
    background: var(--bj-scrim);
  }
  h2 {
    font-size: 1.15rem;
    margin-bottom: var(--space-4);
  }
  .body :global(p:last-child) {
    margin-bottom: 0;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
    margin-top: var(--space-6);
  }
</style>
